import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { BookText, ExternalLink, Settings } from "lucide-react";
import { useNavigate } from "react-router-dom";

function normalizeUrl(u) {
  if (!u) return "";
  return /^https?:\/\//i.test(u) ? u : `https://${u}`;
}

export default function Documentation() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isSuper = user?.role === "super_admin";
  const [data, setData] = useState({ url: "", organization_name: "" });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get("/documentation")
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user?.organization_id]);

  const open = () => {
    const target = normalizeUrl(data.url);
    if (target) window.open(target, "_blank", "noopener,noreferrer");
  };

  return (
    <div data-testid="documentation-page">
      <PageHeader
        eyebrow="[14] Documentation"
        title="Documentation"
        subtitle={`Confluence documentation for ${data.organization_name || user?.organization_name || "this organization"}.`}
        actions={
          data.url ? (
            <Button
              data-testid="doc-open-btn"
              onClick={open}
              className="rounded-none bg-[#F37B2A] hover:bg-[#D86A1F] text-white font-mono text-xs tracking-wider uppercase"
            >
              <ExternalLink className="w-3.5 h-3.5 mr-2" /> Open Documentation
            </Button>
          ) : null
        }
      />

      <div className="p-4 sm:p-6 lg:p-8">
        {loading ? (
          <div className="font-mono text-xs text-[#7A7A7A] py-10 text-center">Loading…</div>
        ) : data.url ? (
          <div data-testid="doc-linked" className="border border-[#E5E0D5] bg-white p-8 max-w-2xl space-y-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 border border-[#E5E0D5] bg-[#F0EDE3] flex items-center justify-center">
                <BookText className="w-5 h-5 text-[#F37B2A]" strokeWidth={1.5} />
              </div>
              <div>
                <div className="text-[#0F0F0F] font-medium tracking-tight">{data.organization_name} documentation</div>
                <div className="uppercase-label mt-0.5">Confluence space</div>
              </div>
            </div>
            <a
              href={normalizeUrl(data.url)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="doc-link"
              className="block font-mono text-sm text-[#F37B2A] hover:text-[#D86A1F] underline decoration-[#F37B2A]/40 hover:decoration-[#D86A1F] break-all"
            >
              {data.url}
            </a>
            <Button
              onClick={open}
              data-testid="doc-open-btn-main"
              className="rounded-none bg-[#F37B2A] hover:bg-[#D86A1F] text-white font-mono text-xs tracking-wider uppercase"
            >
              <ExternalLink className="w-3.5 h-3.5 mr-2" /> Open in Confluence
            </Button>
            <p className="font-mono text-[10px] text-[#9A9A9A]">Opens in a new tab. Confluence uses its own sign-in.</p>
          </div>
        ) : (
          <div data-testid="doc-empty" className="border border-[#E5E0D5] bg-white p-8 max-w-2xl text-center space-y-4">
            <BookText className="w-8 h-8 text-[#C9C2B3] mx-auto" strokeWidth={1.25} />
            <div className="space-y-1">
              <div className="text-[#0F0F0F] font-medium">No documentation link set</div>
              <div className="text-sm text-[#5A5A5A] max-w-md mx-auto">
                {isSuper
                  ? "Add this company's Confluence link in the Admin → Organizations section."
                  : "A documentation link hasn't been added for this organization yet. Please ask your administrator to set it up."}
              </div>
            </div>
            {isSuper && (
              <Button
                data-testid="doc-goto-admin-btn"
                onClick={() => navigate("/admin")}
                className="rounded-none bg-[#F37B2A] hover:bg-[#D86A1F] text-white font-mono text-xs tracking-wider uppercase"
              >
                <Settings className="w-3.5 h-3.5 mr-2" /> Go to Admin
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
